import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Plus, Trash2, Loader2, Save, RefreshCw, BadgePercent, ArrowLeft, ArrowRight, Copy, ClipboardList } from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const inputCls = "w-full bg-ink border border-bone/15 px-3.5 py-2.5 text-sm text-bone placeholder:text-ash/60 focus:border-saffron focus:outline-none transition-colors";
const labelCls = "block text-xs font-display tracking-[0.2em] uppercase text-ash mb-1.5";

function Field({ label, children }) {
  return <div><label className={labelCls}>{label}</label>{children}</div>;
}

function EventTab() {
  const [ev, setEv] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/event").then(({ data }) => setEv({
      ...data,
      tickets: data.tickets?.length ? data.tickets : [{ name: "Regular", price: data.price, admits: 1 }],
    })).catch(() => setEv({ name: "", date: "", time: "", venue: "", price: "", description: "", image_url: "", tickets: [{ name: "Regular", price: 499, admits: 1 }] }));
  }, []);

  if (!ev) return <p className="text-ash py-10" data-testid="event-tab-loading">Loading…</p>;
  const set = (k) => (e) => setEv({ ...ev, [k]: e.target.value });
  const setTier = (i, k, v) => setEv({ ...ev, tickets: ev.tickets.map((t, j) => (j === i ? { ...t, [k]: v } : t)) });
  const addTier = () => setEv({ ...ev, tickets: [...ev.tickets, { name: "", price: "", admits: 1 }] });
  const delTier = (i) => setEv({ ...ev, tickets: ev.tickets.filter((_, j) => j !== i) });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...ev,
        price: Number(ev.price),
        tickets: ev.tickets.filter((t) => t.name).map((t) => ({ name: t.name, price: Number(t.price) || 0, admits: Number(t.admits) || 1 })),
      };
      const { data } = await api.put("/admin/event", payload);
      setEv(data);
      toast.success("Event updated — live on the site now.");
    } catch (err) {
      toast.error(apiError(err, "Could not save event"));
    }
    setSaving(false);
  };

  return (
    <form onSubmit={save} className="max-w-2xl space-y-5" data-testid="event-form">
      <p className="text-sm text-ash">This is the single event shown on the website. Update it as the band moves to the next show.</p>
      <Field label="Event Name">
        <input required value={ev.name} onChange={set("name")} className={inputCls} data-testid="event-name-input" />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Field label="Date">
          <input required type="date" value={ev.date} onChange={set("date")} className={inputCls} data-testid="event-date-input" />
        </Field>
        <Field label="Time">
          <input required value={ev.time} onChange={set("time")} placeholder="7:00 PM" className={inputCls} data-testid="event-time-input" />
        </Field>
        <Field label="Ticket Price (₹)">
          <input required type="number" min="0" value={ev.price} onChange={set("price")} className={inputCls} data-testid="event-price-input" />
        </Field>
      </div>
      <Field label="Venue">
        <input required value={ev.venue} onChange={set("venue")} placeholder="Auditorium, Area, City" className={inputCls} data-testid="event-venue-input" />
      </Field>
      <Field label="Google Maps URL (sent in ticket emails for directions)">
        <input value={ev.maps_url || ""} onChange={set("maps_url")} placeholder="https://maps.google.com/…" className={inputCls} data-testid="event-maps-input" />
      </Field>
      <Field label="Ticket Categories (name · price ₹ · admits how many people)">
        <div className="space-y-2" data-testid="tiers-editor">
          {ev.tickets.map((t, i) => (
            <div key={i} className="flex gap-2 items-center">
              <input value={t.name} onChange={(e) => setTier(i, "name", e.target.value)} className={inputCls} placeholder="Early Bird" data-testid={`tier-name-${i}`} />
              <input type="number" min="0" value={t.price} onChange={(e) => setTier(i, "price", e.target.value)} className={`${inputCls} w-28`} placeholder="₹" data-testid={`tier-price-${i}`} />
              <input type="number" min="1" value={t.admits} onChange={(e) => setTier(i, "admits", e.target.value)} className={`${inputCls} w-24`} title="Admits" data-testid={`tier-admits-${i}`} />
              <button type="button" onClick={() => delTier(i)} className="text-ash hover:text-red-400 transition-colors shrink-0" data-testid={`tier-del-${i}`}><Trash2 size={15} /></button>
            </div>
          ))}
          <button type="button" onClick={addTier} className="text-xs text-saffron hover:text-gold transition-colors" data-testid="tier-add-btn">+ Add category</button>
        </div>
      </Field>
      <Field label="Description (optional)">
        <textarea rows={3} value={ev.description} onChange={set("description")} className={inputCls} data-testid="event-description-input" />
      </Field>
      <Field label="Poster Image URL (optional)">
        <input value={ev.image_url} onChange={set("image_url")} placeholder="https://…" className={inputCls} data-testid="event-image-input" />
      </Field>
      <div className="border border-bone/10 p-4 space-y-4" data-testid="partner-links-fieldset">
        <p className="text-sm text-ash">Also available on — paste your event's listing links. Badges appear on the booking page only for the ones you fill.</p>
        <Field label="BookMyShow URL">
          <input value={ev.bookmyshow_url || ""} onChange={set("bookmyshow_url")} placeholder="https://in.bookmyshow.com/…" className={inputCls} data-testid="event-bms-input" />
        </Field>
        <Field label="District URL">
          <input value={ev.district_url || ""} onChange={set("district_url")} placeholder="https://district.in/…" className={inputCls} data-testid="event-district-input" />
        </Field>
        <Field label="Swiggy URL">
          <input value={ev.swiggy_url || ""} onChange={set("swiggy_url")} placeholder="https://swiggy.com/…" className={inputCls} data-testid="event-swiggy-input" />
        </Field>
      </div>
      <button type="submit" disabled={saving}
        className="flex items-center gap-2 px-8 py-3 bg-saffron text-ink text-sm font-medium hover:bg-gold transition-colors disabled:opacity-50"
        data-testid="event-save-btn">
        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
        {saving ? "Saving…" : "Save Event"}
      </button>
    </form>
  );
}

function VideosTab() {
  const [videos, setVideos] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", url: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/videos").then(({ data }) => setVideos(data)), []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/videos", form);
      toast.success("Video embedded on the site.");
      setForm({ title: "", url: "" });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err, "Could not add video"));
    }
    setSaving(false);
  };

  const remove = async (id) => {
    await api.delete(`/admin/videos/${id}`);
    load();
  };

  return (
    <div data-testid="videos-tab">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-ash">Paste an Instagram reel or YouTube video/shorts link — the player is detected automatically.</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="flex items-center gap-2 px-5 py-2.5 bg-saffron text-ink text-sm font-medium hover:bg-gold transition-colors" data-testid="add-video-btn">
              <Plus size={15} /> Add Video
            </button>
          </DialogTrigger>
          <DialogContent className="bg-surface border-bone/15 text-bone">
            <DialogHeader><DialogTitle className="font-serif text-2xl">Add Video</DialogTitle></DialogHeader>
            <form onSubmit={add} className="space-y-4 mt-2" data-testid="video-form">
              <Field label="Title">
                <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} placeholder="Mumbai Sandhya — finale aarti" data-testid="video-title-input" />
              </Field>
              <Field label="Instagram or YouTube Link">
                <input required value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className={inputCls} placeholder="instagram.com/reel/… · youtube.com/watch?v=… · youtube.com/shorts/…" data-testid="video-url-input" />
              </Field>
              <button type="submit" disabled={saving} className="w-full py-3 bg-saffron text-ink text-sm font-medium hover:bg-gold transition-colors disabled:opacity-50" data-testid="video-save-btn">
                {saving ? "Embedding…" : "Embed Video"}
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      {videos.length === 0 && <p className="text-ash py-8" data-testid="videos-empty">No videos yet. Add your first video above.</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {videos.map((v, i) => {
          const platform = v.platform || "instagram";
          return (
            <div key={v.id} className="border border-bone/10 bg-ink" data-testid={`admin-video-${i}`}>
              <div className={v.orientation === "landscape" ? "aspect-video" : "aspect-[9/12] max-h-[320px]"}>
                <iframe src={v.embed_url} className="ig-embed-frame" title={v.title} loading="lazy" allowFullScreen />
              </div>
              <div className="p-3 flex items-center justify-between gap-2 border-t border-bone/10">
                <p className="text-xs text-bone/80 truncate flex items-center gap-2 min-w-0">
                  <span className={`text-[9px] font-display px-1.5 py-0.5 shrink-0 ${platform === "youtube" ? "bg-red-500/15 text-red-400" : "bg-saffron/15 text-saffron"}`} data-testid={`admin-video-platform-${i}`}>
                    {platform.toUpperCase()}
                  </span>
                  <span className="truncate">{v.title}</span>
                </p>
                <button onClick={() => remove(v.id)} className="text-ash hover:text-red-400 transition-colors shrink-0" data-testid={`delete-video-${i}`}><Trash2 size={15} /></button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function GalleryTab() {
  const [photos, setPhotos] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ image_url: "", caption: "" });
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(null);

  const load = useCallback(() => api.get("/gallery").then(({ data }) => setPhotos(data)), []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (files.length) {
        for (let i = 0; i < files.length; i++) {
          setProgress({ done: i, total: files.length });
          const fd = new FormData();
          fd.append("file", files[i]);
          fd.append("caption", form.caption);
          await api.post("/admin/gallery/upload", fd);
        }
        toast.success(files.length === 1 ? "Photo added to gallery." : `${files.length} photos added to gallery.`);
      } else {
        await api.post("/admin/gallery", form);
        toast.success("Photo added to gallery.");
      }
      setForm({ image_url: "", caption: "" });
      setFiles([]);
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err, "Could not add photo"));
    }
    setProgress(null);
    setSaving(false);
  };

  const remove = async (id) => { await api.delete(`/admin/gallery/${id}`); load(); };

  return (
    <div data-testid="gallery-tab">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-ash">Upload raw photos from your phone/camera, or paste an image URL.</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="flex items-center gap-2 px-5 py-2.5 bg-saffron text-ink text-sm font-medium hover:bg-gold transition-colors" data-testid="add-photo-btn">
              <Plus size={15} /> Add Photo
            </button>
          </DialogTrigger>
          <DialogContent className="bg-surface border-bone/15 text-bone">
            <DialogHeader><DialogTitle className="font-serif text-2xl">Add Photo</DialogTitle></DialogHeader>
            <form onSubmit={add} className="space-y-4 mt-2" data-testid="photo-form">
              <Field label="Upload Photos (jpg / png / webp, max 15 MB each — select many at once)">
                <input type="file" accept="image/*" multiple onChange={(e) => setFiles(Array.from(e.target.files || []))}
                  className="w-full text-sm text-ash file:mr-4 file:px-4 file:py-2.5 file:bg-saffron file:text-ink file:border-0 file:text-sm file:font-medium file:cursor-pointer"
                  data-testid="photo-file-input" />
              </Field>
              {files.length > 0 && (
                <p className="text-xs text-saffron" data-testid="photo-files-count">{files.length} photo{files.length > 1 ? "s" : ""} selected</p>
              )}
              {files.length === 0 && (
                <Field label="Or Image URL">
                  <input required value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className={inputCls} placeholder="https://…" data-testid="photo-url-input" />
                </Field>
              )}
              <Field label="Caption (optional — applied to all selected photos)">
                <input value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} className={inputCls} data-testid="photo-caption-input" />
              </Field>
              <button type="submit" disabled={saving || (!files.length && !form.image_url)} className="w-full py-3 bg-saffron text-ink text-sm font-medium hover:bg-gold transition-colors disabled:opacity-50" data-testid="photo-save-btn">
                {saving
                  ? (progress ? `Uploading ${progress.done + 1} of ${progress.total}…` : "Uploading…")
                  : files.length > 1 ? `Upload ${files.length} Photos` : files.length === 1 ? "Upload to Gallery" : "Add to Gallery"}
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {photos.map((p, i) => (
          <div key={p.id} className="group relative border border-bone/10 aspect-square overflow-hidden" data-testid={`admin-photo-${i}`}>
            <img src={fileUrl(p.image_url)} alt={p.caption} className="w-full h-full object-cover" loading="lazy" />
            <button onClick={() => remove(p.id)}
              className="absolute top-2 right-2 p-1.5 bg-ink/80 text-ash hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid={`delete-photo-${i}`}><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

const STATUS_CLS = { new: "text-gold", contacted: "text-bone", confirmed: "text-green-400" };

function parseHalfPrice(pkg) {
  const m = pkg && pkg.match(/₹([\d,]+)/);
  return m ? Math.round(parseInt(m[1].replace(/,/g, ""), 10) / 2) : "";
}

function EnquiriesTab() {
  const [items, setItems] = useState([]);
  const [advFor, setAdvFor] = useState(null);
  const [advAmount, setAdvAmount] = useState("");
  const [advLink, setAdvLink] = useState("");
  const [advBusy, setAdvBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/enquiries").then(({ data }) => setItems(data)), []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/enquiries/${id}`, { status });
    load();
  };

  const openAdvance = (q) => {
    setAdvFor(q);
    setAdvAmount(parseHalfPrice(q.package));
    setAdvLink(q.advance_link || "");
  };

  const createAdvance = async () => {
    setAdvBusy(true);
    try {
      const { data } = await api.post(`/admin/enquiries/${advFor.id}/advance-link`, { amount: Number(advAmount) });
      setAdvLink(data.link);
      toast.success("Advance link created and emailed to the client.");
      load();
    } catch (err) {
      toast.error(apiError(err, "Could not create payment link"));
    }
    setAdvBusy(false);
  };

  return (
    <div data-testid="enquiries-tab">
      {items.length === 0 && <p className="text-ash py-8" data-testid="enquiries-empty">No enquiries yet.</p>}
      {items.length > 0 && (
        <div className="overflow-x-auto border border-bone/10">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-bone/10 text-left font-display text-xs tracking-[0.15em] uppercase text-ash">
                <th className="p-4">Date</th><th className="p-4">Name</th><th className="p-4">Contact</th><th className="p-4">Event</th><th className="p-4">Package</th><th className="p-4">City / Date</th><th className="p-4">Message</th><th className="p-4">Advance</th><th className="p-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((q, i) => (
                <tr key={q.id} className="border-b border-bone/5 hover:bg-surface/60 transition-colors" data-testid={`enquiry-row-${i}`}>
                  <td className="p-4 text-ash whitespace-nowrap">{new Date(q.created_at).toLocaleDateString("en-IN")}</td>
                  <td className="p-4 text-bone whitespace-nowrap">{q.name}</td>
                  <td className="p-4 text-ash">{q.email}<br />{q.phone}</td>
                  <td className="p-4 text-bone/80 whitespace-nowrap">{q.event_type}</td>
                  <td className="p-4 text-saffron/90 text-xs max-w-[180px]">{q.package || "—"}</td>
                  <td className="p-4 text-ash whitespace-nowrap">{q.city || "—"}{q.event_date ? ` · ${q.event_date}` : ""}</td>
                  <td className="p-4 text-ash max-w-[220px] truncate">{q.message || "—"}</td>
                  <td className="p-4 whitespace-nowrap">
                    {q.advance_status === "paid" ? (
                      <span className="text-xs text-green-400 font-medium" data-testid={`enquiry-advance-paid-${i}`}>
                        Advance paid ₹{(q.advance_amount || 0).toLocaleString("en-IN")}
                      </span>
                    ) : q.advance_link ? (
                      <span className="flex items-center gap-2">
                        <a href={q.advance_link} target="_blank" rel="noreferrer" className="text-xs text-gold underline underline-offset-2" data-testid={`enquiry-advance-view-${i}`}>
                          ₹{(q.advance_amount || 0).toLocaleString("en-IN")} sent
                        </a>
                        <button onClick={async () => { const { data } = await api.post(`/admin/enquiries/${q.id}/advance-refresh`); if (data.advance_status === "paid") { toast.success("Advance paid!"); load(); } else toast.info("Not paid yet"); }}
                          title="Check payment status"
                          className="text-ash hover:text-saffron transition-colors" data-testid={`enquiry-advance-refresh-${i}`}>
                          <RefreshCw size={13} />
                        </button>
                      </span>
                    ) : (
                      <button onClick={() => openAdvance(q)} className="text-xs px-3 py-1.5 border border-saffron/40 text-saffron hover:bg-saffron hover:text-ink transition-colors" data-testid={`enquiry-advance-btn-${i}`}>
                        50% Advance
                      </button>
                    )}
                  </td>
                  <td className="p-4">
                    <select value={q.status} onChange={(e) => setStatus(q.id, e.target.value)}
                      className={`bg-ink border border-bone/15 px-2 py-1.5 text-xs focus:outline-none focus:border-saffron ${STATUS_CLS[q.status] || "text-bone"}`}
                      data-testid={`enquiry-status-${i}`}>
                      <option value="new" className="bg-ink">New</option>
                      <option value="contacted" className="bg-ink">Contacted</option>
                      <option value="confirmed" className="bg-ink">Confirmed</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!advFor} onOpenChange={(o) => { if (!o) { setAdvFor(null); setAdvLink(""); } }}>
        <DialogContent className="bg-surface border-bone/15 text-bone">
          <DialogHeader><DialogTitle className="font-serif text-2xl">50% Advance Payment</DialogTitle></DialogHeader>
          {advFor && (
            <div className="space-y-4 mt-2">
              <p className="text-sm text-ash">{advFor.name} · {advFor.package || "No package selected"}</p>
              <Field label="Advance Amount (₹)">
                <input type="number" min="1" value={advAmount} onChange={(e) => setAdvAmount(e.target.value)} className={inputCls} data-testid="advance-amount-input" />
              </Field>
              {advLink ? (
                <div className="border border-saffron/40 p-4" data-testid="advance-link-box">
                  <p className="text-xs text-ash mb-2">Payment link — also emailed to the client:</p>
                  <a href={advLink} target="_blank" rel="noreferrer" className="text-gold text-sm break-all" data-testid="advance-link-url">{advLink}</a>
                </div>
              ) : (
                <button onClick={createAdvance} disabled={advBusy || !advAmount}
                  className="w-full py-3 bg-saffron text-ink text-sm font-medium hover:bg-gold transition-colors disabled:opacity-50"
                  data-testid="advance-create-btn">
                  {advBusy ? "Creating…" : "Create & Email Payment Link"}
                </button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GuestsTab() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [passOpen, setPassOpen] = useState(false);
  const [passEvents, setPassEvents] = useState([]);
  const [passForm, setPassForm] = useState({ name: "", phone: "", show_id: "", tier: "", quantity: 1 });
  const [issuing, setIssuing] = useState(false);
  const load = useCallback(() => api.get("/admin/bookings").then(({ data }) => setItems(data)), []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  useEffect(() => {
    if (!passOpen) return;
    Promise.all([api.get("/event"), api.get("/shows")]).then(([ev, sh]) => {
      const opts = [{ id: "", name: `${ev.data.name} (current event)`, tickets: ev.data.tickets || [] }];
      (sh.data || []).filter((s) => s.ticketed).forEach((s) => opts.push({ id: s.id, name: s.name, tickets: s.tickets || [] }));
      setPassEvents(opts);
    }).catch(() => {});
  }, [passOpen]);

  const selEvent = passEvents.find((e) => e.id === passForm.show_id) || passEvents[0];
  const tiers = (selEvent?.tickets?.length ? selEvent.tickets : [{ name: "Regular", admits: 1 }]);
  const selTier = tiers.find((t) => t.name === passForm.tier) || tiers[0];

  const issuePass = async (e) => {
    e.preventDefault();
    setIssuing(true);
    try {
      const { data } = await api.post("/admin/bookings/issue-pass", {
        name: passForm.name, phone: passForm.phone,
        tier: selTier.name, quantity: Number(passForm.quantity) || 1, show_id: passForm.show_id,
      });
      toast.success(`Pass ${data.ticket_code} issued — WhatsApp on its way to ${data.phone}`);
      setPassOpen(false);
      setPassForm({ name: "", phone: "", show_id: "", tier: "", quantity: 1 });
      load();
    } catch (err) {
      toast.error(apiError(err, "Could not issue pass"));
    }
    setIssuing(false);
  };

  const act = async (id, action) => {
    if (action === "cancel" && !window.confirm("Cancel this ticket? The customer's code will stop working.")) return;
    await api.patch(`/admin/bookings/${id}`, { action });
    toast.success(action === "checkin" ? "Checked in." : action === "cancel" ? "Ticket cancelled." : "Check-in undone.");
    load();
  };

  const q = search.trim().toLowerCase();
  const filtered = items.filter((b) => !q || (b.phone || "").includes(q) || (b.name || "").toLowerCase().includes(q) || (b.ticket_code || "").toLowerCase().includes(q));

  return (
    <div data-testid="guests-tab">
      <div className="flex items-center justify-between gap-4 mb-6">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by phone, name or ticket code…"
          className={`${inputCls} max-w-md`} data-testid="guest-search-input" />
        <div className="flex items-center gap-3">
          <p className="text-xs text-ash whitespace-nowrap">{filtered.length} booking{filtered.length !== 1 ? "s" : ""}</p>
          <Dialog open={passOpen} onOpenChange={setPassOpen}>
            <DialogTrigger asChild>
              <button className="flex items-center gap-2 px-5 py-2.5 bg-saffron text-ink text-sm font-medium hover:bg-gold transition-colors whitespace-nowrap" data-testid="issue-pass-btn">
                <Plus size={15} /> Issue Free Pass
              </button>
            </DialogTrigger>
            <DialogContent className="bg-surface border-bone/15 text-bone max-w-md">
              <DialogHeader><DialogTitle className="font-serif text-2xl">Issue Free Pass</DialogTitle></DialogHeader>
              <p className="text-xs text-ash">For offline sales and complimentary guests. The ticket goes straight to their WhatsApp with the QR.</p>
              <form onSubmit={issuePass} className="space-y-4 mt-2" data-testid="issue-pass-form">
                <Field label="Guest Name"><input required value={passForm.name} onChange={(e) => setPassForm({ ...passForm, name: e.target.value })} className={inputCls} data-testid="pass-name-input" /></Field>
                <Field label="WhatsApp Number"><input required type="tel" value={passForm.phone} onChange={(e) => setPassForm({ ...passForm, phone: e.target.value })} placeholder="98765 43210" className={inputCls} data-testid="pass-phone-input" /></Field>
                <Field label="Event">
                  <select value={passForm.show_id} onChange={(e) => setPassForm({ ...passForm, show_id: e.target.value, tier: "" })}
                    className={inputCls} data-testid="pass-event-select">
                    {passEvents.map((ev) => <option key={ev.id || "current"} value={ev.id} className="bg-ink">{ev.name}</option>)}
                  </select>
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Category">
                    <select value={selTier?.name || ""} onChange={(e) => setPassForm({ ...passForm, tier: e.target.value })}
                      className={inputCls} data-testid="pass-tier-select">
                      {tiers.map((t) => <option key={t.name} value={t.name} className="bg-ink">{t.name}{t.admits > 1 ? ` (admits ${t.admits})` : ""}</option>)}
                    </select>
                  </Field>
                  <Field label="Quantity">
                    <input type="number" min="1" max="20" value={passForm.quantity} onChange={(e) => setPassForm({ ...passForm, quantity: e.target.value })} className={inputCls} data-testid="pass-qty-input" />
                  </Field>
                </div>
                <p className="text-xs text-ash">Headcount: {(selTier?.admits || 1) * (Number(passForm.quantity) || 1)} guest{(selTier?.admits || 1) * (Number(passForm.quantity) || 1) !== 1 ? "s" : ""} · ₹0 · appears in guest list as confirmed</p>
                <button type="submit" disabled={issuing} className="w-full py-3 bg-saffron text-ink text-sm font-medium hover:bg-gold transition-colors disabled:opacity-50" data-testid="pass-submit-btn">
                  {issuing ? "Issuing…" : "Issue Pass & Send WhatsApp"}
                </button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      {filtered.length === 0 && <p className="text-ash py-8" data-testid="guests-empty">No bookings found.</p>}
      {filtered.length > 0 && (
        <div className="overflow-x-auto border border-bone/10">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-bone/10 text-left font-display text-xs tracking-[0.15em] uppercase text-ash">
                <th className="p-4">Ticket</th><th className="p-4">Guest</th><th className="p-4">Phone</th><th className="p-4">Category</th><th className="p-4">Headcount</th><th className="p-4">Amount</th><th className="p-4">Status</th><th className="p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b, i) => (
                <tr key={b.id} className="border-b border-bone/5 hover:bg-surface/60 transition-colors" data-testid={`guest-row-${i}`}>
                  <td className="p-4 font-medium text-gold whitespace-nowrap">{b.ticket_code || "—"}</td>
                  <td className="p-4 text-bone whitespace-nowrap">{b.name}</td>
                  <td className="p-4 text-ash whitespace-nowrap">{b.phone}</td>
                  <td className="p-4 text-bone/80 whitespace-nowrap">{b.tier || "Regular"} × {b.quantity}</td>
                  <td className="p-4 text-bone/80">{b.admits || b.quantity}</td>
                  <td className="p-4 text-bone/80 whitespace-nowrap">{b.amount ? `₹${(b.amount / 100).toLocaleString("en-IN")}` : "Free"}{b.origin === "admin_pass" ? <span className="text-[10px] font-display text-saffron border border-saffron/50 px-1.5 py-0.5 ml-2">PASS</span> : null}</td>
                  <td className="p-4 whitespace-nowrap">
                    {b.status === "cancelled" ? <span className="text-xs text-red-400">Cancelled</span>
                      : b.status !== "confirmed" ? <span className="text-xs text-ash">Unpaid</span>
                      : b.checked_in ? <span className="text-xs text-green-400">Checked in</span>
                      : <span className="text-xs text-gold">Confirmed</span>}
                  </td>
                  <td className="p-4 whitespace-nowrap">
                    {b.status === "confirmed" && (
                      <span className="flex gap-2">
                        {b.checked_in ? (
                          <button onClick={() => act(b.id, "uncheckin")} className="text-xs px-3 py-1.5 border border-bone/20 text-ash hover:text-bone transition-colors" data-testid={`guest-uncheckin-${i}`}>Undo</button>
                        ) : (
                          <button onClick={() => act(b.id, "checkin")} className="text-xs px-3 py-1.5 bg-saffron text-ink font-medium hover:bg-gold transition-colors" data-testid={`guest-checkin-${i}`}>Check-in</button>
                        )}
                        <button onClick={() => act(b.id, "cancel")} className="text-xs px-3 py-1.5 border border-red-400/40 text-red-400 hover:bg-red-400/10 transition-colors" data-testid={`guest-cancel-${i}`}>Cancel Ticket</button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const emptyShow = { name: "", date: "", time: "7:00 PM", venue: "", description: "", image_url: "", maps_url: "", ticketed: false, price: 0, tiersText: "", bookmyshow_url: "", district_url: "", swiggy_url: "", published: true };

function ShowsTab() {
  const [shows, setShows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyShow);
  const [saving, setSaving] = useState(false);
  const [posterFile, setPosterFile] = useState(null);

  const load = useCallback(() => api.get("/admin/shows").then(({ data }) => setShows(data)), []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const openEdit = (s) => {
    setEditing(s);
    setPosterFile(null);
    setForm({
      name: s.name, date: s.date, time: s.time, venue: s.venue,
      description: s.description || "", image_url: s.image_url || "", maps_url: s.maps_url || "",
      bookmyshow_url: s.bookmyshow_url || "", district_url: s.district_url || "", swiggy_url: s.swiggy_url || "",
      ticketed: !!s.ticketed, price: s.price || 0,
      tiersText: (s.tickets || []).map((t) => `${t.name}, ${t.price}, ${t.admits ?? 1}`).join("\n"),
      published: s.published,
    });
    setOpen(true);
  };

  const openAdd = () => { setEditing(null); setForm(emptyShow); setPosterFile(null); setOpen(true); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const tickets = form.tiersText.split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [name, price, admits] = line.split(",").map((x) => x.trim());
        return { name, price: Number(price) || 0, admits: Number(admits) || 1 };
      });
    let imageUrl = form.image_url;
    if (posterFile) {
      const fd = new FormData();
      fd.append("file", posterFile);
      const { data: up } = await api.post("/admin/shows/upload-poster", fd);
      imageUrl = up.image_url;
    }
    const payload = {
      name: form.name, date: form.date, time: form.time, venue: form.venue,
      description: form.description, image_url: imageUrl, maps_url: form.maps_url,
      bookmyshow_url: form.bookmyshow_url, district_url: form.district_url, swiggy_url: form.swiggy_url,
      ticketed: !!form.ticketed, price: Number(form.price) || 0,
      tickets: form.ticketed ? tickets : [],
      published: !!form.published,
    };
    try {
      if (editing) await api.put(`/admin/shows/${editing.id}`, payload);
      else await api.post("/admin/shows", payload);
      toast.success("Show saved — live on /upcomingshows.");
      setPosterFile(null);
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err, "Could not save show"));
    }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this show?")) return;
    await api.delete(`/admin/shows/${id}`);
    load();
  };

  return (
    <div data-testid="shows-tab">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-ash">All shows appear on /upcomingshows. Tick the "Ticketed" option to sell tickets for a show via /book.</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button onClick={openAdd} className="flex items-center gap-2 px-5 py-2.5 bg-saffron text-ink text-sm font-medium hover:bg-gold transition-colors" data-testid="add-show-btn">
              <Plus size={15} /> Add Show
            </button>
          </DialogTrigger>
          <DialogContent className="bg-surface border-bone/15 text-bone max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-serif text-2xl">{editing ? "Edit Show" : "Add Show"}</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-4 mt-2" data-testid="show-form">
              <Field label="Show Name"><input required value={form.name} onChange={set("name")} className={inputCls} data-testid="show-name-input" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Date"><input required type="date" value={form.date} onChange={set("date")} className={inputCls} data-testid="show-date-input" /></Field>
                <Field label="Time"><input required value={form.time} onChange={set("time")} className={inputCls} data-testid="show-time-input" /></Field>
              </div>
              <Field label="Venue"><input required value={form.venue} onChange={set("venue")} className={inputCls} data-testid="show-venue-input" /></Field>
              <Field label="Google Maps URL (for directions)">
                <input value={form.maps_url} onChange={set("maps_url")} placeholder="https://maps.google.com/…" className={inputCls} data-testid="show-maps-input" />
              </Field>
              <div className="border border-bone/10 p-3.5 space-y-3" data-testid="show-partner-fieldset">
                <p className="text-xs text-ash">Also available on — badges show on the booking page only for links you fill.</p>
                <Field label="BookMyShow URL"><input value={form.bookmyshow_url} onChange={set("bookmyshow_url")} placeholder="https://in.bookmyshow.com/…" className={inputCls} data-testid="show-bms-input" /></Field>
                <Field label="District URL"><input value={form.district_url} onChange={set("district_url")} placeholder="https://district.in/…" className={inputCls} data-testid="show-district-input" /></Field>
                <Field label="Swiggy URL"><input value={form.swiggy_url} onChange={set("swiggy_url")} placeholder="https://swiggy.com/…" className={inputCls} data-testid="show-swiggy-input" /></Field>
              </div>
              <Field label="Description"><textarea rows={2} value={form.description} onChange={set("description")} className={inputCls} data-testid="show-description-input" /></Field>
              <Field label="Poster Image — upload artwork or paste a URL">
                <input type="file" accept="image/*" onChange={(e) => setPosterFile(e.target.files[0] || null)}
                  className="w-full text-sm text-ash file:mr-4 file:px-4 file:py-2.5 file:bg-saffron file:text-ink file:border-0 file:text-sm file:font-medium file:cursor-pointer"
                  data-testid="show-poster-input" />
              </Field>
              {posterFile ? (
                <img src={URL.createObjectURL(posterFile)} alt="Poster preview" className="h-32 object-cover border border-bone/15" data-testid="show-poster-preview" />
              ) : form.image_url ? (
                <img src={fileUrl(form.image_url)} alt="Current poster" className="h-32 object-cover border border-bone/15" data-testid="show-poster-preview" />
              ) : null}
              {!posterFile && (
                <Field label="Or Poster Image URL">
                  <input value={form.image_url} onChange={set("image_url")} placeholder="https://…" className={inputCls} data-testid="show-image-input" />
                </Field>
              )}
              <label className="flex items-center gap-2 text-sm text-ash cursor-pointer">
                <input type="checkbox" checked={form.ticketed} onChange={(e) => setForm({ ...form, ticketed: e.target.checked })} data-testid="show-ticketed-input" />
                Ticketed show (sells tickets via /book)
              </label>
              {form.ticketed && (
                <>
                  <Field label="Base Price (₹)"><input type="number" min="0" value={form.price} onChange={set("price")} className={inputCls} data-testid="show-price-input" /></Field>
                  <Field label="Ticket categories — one per line: Name, Price, Admits">
                    <textarea rows={4} value={form.tiersText} onChange={set("tiersText")} className={inputCls} placeholder={"Early Bird, 399, 1\nRegular, 499, 1\nGroup of 4, 1599, 4"} data-testid="show-tiers-input" />
                  </Field>
                </>
              )}
              <label className="flex items-center gap-2 text-sm text-ash cursor-pointer">
                <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="show-published-input" />
                Published (visible on the site)
              </label>
              <button type="submit" disabled={saving} className="w-full py-3 bg-saffron text-ink text-sm font-medium hover:bg-gold transition-colors disabled:opacity-50" data-testid="show-save-btn">
                {saving ? "Saving…" : "Save Show"}
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      {shows.length === 0 && <p className="text-ash py-8">No shows yet.</p>}
      <div className="space-y-3">
        {shows.map((s, i) => (
          <div key={s.id} className="flex items-center justify-between gap-4 border border-bone/10 p-4" data-testid={`admin-show-${i}`}>
            <div>
              <p className="text-bone font-medium">{s.name} {s.ticketed && <span className="text-[10px] font-display text-ink bg-saffron px-1.5 py-0.5 ml-1">TICKETED</span>} {!s.published && <span className="text-xs text-ash">(hidden)</span>}</p>
              <p className="text-xs text-ash mt-1">{s.date} · {s.time} · {s.venue}{s.maps_url ? " · maps linked" : ""}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => openEdit(s)} className="text-xs px-3 py-1.5 border border-bone/20 text-bone hover:border-saffron transition-colors" data-testid={`edit-show-${i}`}>Edit</button>
              <button onClick={() => remove(s.id)} className="text-xs px-3 py-1.5 border border-red-400/40 text-red-400 hover:bg-red-400/10 transition-colors" data-testid={`delete-show-${i}`}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const emptyPkg = { name: "", group: "Within Hyderabad", price: "", lineupText: "", featured: false, master: false };

function PackagesTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPkg);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/packages").then(({ data }) => setItems(data)), []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const openEdit = (p) => { setEditing(p); setForm({ name: p.name, group: p.group, price: p.price, lineupText: (p.lineup || []).join("\n"), featured: !!p.featured, master: !!p.master }); setOpen(true); };
  const openAdd = () => { setEditing(null); setForm(emptyPkg); setOpen(true); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { name: form.name, group: form.group, price: Number(form.price) || 0, featured: !!form.featured, master: !!form.master, lineup: form.lineupText.split("\n").map((l) => l.trim()).filter(Boolean) };
    try {
      if (editing) await api.put(`/admin/packages/${editing.id}`, payload);
      else await api.post("/admin/packages", payload);
      toast.success("Package saved — live on the site.");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err, "Could not save package"));
    }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this package?")) return;
    await api.delete(`/admin/packages/${id}`);
    load();
  };

  return (
    <div data-testid="packages-tab">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-ash">These packages appear in the Packages section and on /bookprivateshow.</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button onClick={openAdd} className="flex items-center gap-2 px-5 py-2.5 bg-saffron text-ink text-sm font-medium hover:bg-gold transition-colors" data-testid="add-package-btn">
              <Plus size={15} /> Add Package
            </button>
          </DialogTrigger>
          <DialogContent className="bg-surface border-bone/15 text-bone">
            <DialogHeader><DialogTitle className="font-serif text-2xl">{editing ? "Edit Package" : "Add Package"}</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-4 mt-2" data-testid="package-form">
              <Field label="Package Name"><input required value={form.name} onChange={set("name")} className={inputCls} data-testid="pkg-name-input" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Location Group">
                  <select value={form.group} onChange={set("group")} className={inputCls} data-testid="pkg-group-select">
                    <option value="Within Hyderabad" className="bg-ink">Within Hyderabad</option>
                    <option value="Outside Hyderabad" className="bg-ink">Outside Hyderabad</option>
                  </select>
                </Field>
                <Field label="Price (₹)"><input required type="number" min="0" value={form.price} onChange={set("price")} className={inputCls} data-testid="pkg-price-input" /></Field>
              </div>
              <Field label="Inclusions — one per line">
                <textarea rows={5} value={form.lineupText} onChange={set("lineupText")} className={inputCls} placeholder={"2 Male Singers\nKeyboard\nDholak + Darbuka"} data-testid="pkg-lineup-input" />
              </Field>
              <label className="flex items-center gap-2 text-sm text-ash cursor-pointer">
                <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="pkg-featured-input" />
                Mark as Popular
              </label>
              <label className="flex items-center gap-2 text-sm text-ash cursor-pointer">
                <input type="checkbox" checked={form.master} onChange={(e) => setForm({ ...form, master: e.target.checked })} data-testid="pkg-master-input" />
                Show as the single offer on the Book Private Show page
              </label>
              <p className="text-xs text-ash">Only one package can be the offer — marking this one unmarks the others.</p>
              <button type="submit" disabled={saving} className="w-full py-3 bg-saffron text-ink text-sm font-medium hover:bg-gold transition-colors disabled:opacity-50" data-testid="pkg-save-btn">
                {saving ? "Saving…" : "Save Package"}
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="space-y-3">
        {items.map((p, i) => (
          <div key={p.id} className="flex items-center justify-between gap-4 border border-bone/10 p-4" data-testid={`admin-package-${i}`}>
            <div>
              <p className="text-bone font-medium">{p.name} {p.featured && <span className="text-[10px] font-display text-ink bg-gold px-1.5 py-0.5 ml-1">POPULAR</span>} {p.master && <span className="text-[10px] font-display text-ink bg-saffron px-1.5 py-0.5 ml-1" data-testid={`pkg-master-badge-${i}`}>BOOKING PAGE OFFER</span>}</p>
              <p className="text-xs text-ash mt-1">{p.group} · ₹{p.price.toLocaleString("en-IN")} · {(p.lineup || []).length} inclusions</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => openEdit(p)} className="text-xs px-3 py-1.5 border border-bone/20 text-bone hover:border-saffron transition-colors" data-testid={`edit-package-${i}`}>Edit</button>
              <button onClick={() => remove(p.id)} className="text-xs px-3 py-1.5 border border-red-400/40 text-red-400 hover:bg-red-400/10 transition-colors" data-testid={`delete-package-${i}`}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FaqsManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ question: "", answer: "", active: true });
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ question: "", answer: "", active: true });

  const load = useCallback(() => {
    api.get("/admin/faqs").then(({ data }) => setItems(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/faqs", form);
      toast.success("FAQ added.");
      setForm({ question: "", answer: "", active: true });
      load();
    } catch (err) {
      toast.error(apiError(err, "Could not add FAQ"));
    }
    setSaving(false);
  };

  const startEdit = (f) => {
    setEditId(f.id);
    setEditForm({ question: f.question, answer: f.answer, active: f.active });
  };

  const saveEdit = async () => {
    try {
      await api.put(`/admin/faqs/${editId}`, editForm);
      toast.success("FAQ updated.");
      setEditId(null);
      load();
    } catch (err) {
      toast.error(apiError(err, "Could not update FAQ"));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this FAQ?")) return;
    await api.delete(`/admin/faqs/${id}`);
    load();
  };

  const toggle = async (f) => {
    await api.put(`/admin/faqs/${f.id}`, { question: f.question, answer: f.answer, active: !f.active });
    load();
  };

  const move = async (id, direction) => {
    try {
      await api.post(`/admin/faqs/${id}/move`, null, { params: { direction } });
      load();
    } catch {
      toast.error("Could not reorder");
    }
  };

  return (
    <div className="border-t border-bone/10 pt-6 space-y-4" data-testid="faqs-manager">
      <div>
        <p className="text-sm text-bone font-medium">FAQs — Book Private Show page</p>
        <p className="text-xs text-ash mt-1">Only active FAQs are shown publicly, in this order.</p>
      </div>
      <div className="space-y-3">
        {items.map((f, i) => (
          <div key={f.id} className="border border-bone/10 p-4 space-y-2.5" data-testid={`admin-faq-${i}`}>
            {editId === f.id ? (
              <>
                <input value={editForm.question} onChange={(e) => setEditForm({ ...editForm, question: e.target.value })}
                  placeholder="Question" className={inputCls} data-testid={`faq-edit-question-${i}`} />
                <textarea rows={2} value={editForm.answer} onChange={(e) => setEditForm({ ...editForm, answer: e.target.value })}
                  placeholder="Answer" className={inputCls} data-testid={`faq-edit-answer-${i}`} />
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 text-xs text-ash cursor-pointer">
                    <input type="checkbox" checked={editForm.active} onChange={(e) => setEditForm({ ...editForm, active: e.target.checked })} data-testid={`faq-edit-active-${i}`} />
                    Active
                  </label>
                  <button onClick={saveEdit} className="text-xs px-3 py-1.5 bg-saffron text-ink font-bold hover:bg-gold transition-colors" data-testid={`faq-edit-save-${i}`}>Save</button>
                  <button onClick={() => setEditId(null)} className="text-xs px-3 py-1.5 border border-bone/20 text-bone hover:border-saffron transition-colors" data-testid={`faq-edit-cancel-${i}`}>Cancel</button>
                </div>
              </>
            ) : (
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm text-bone flex items-center gap-2 flex-wrap">
                    {f.question}
                    <span className={`text-[9px] font-display px-1.5 py-0.5 ${f.active ? "bg-saffron text-ink" : "bg-bone/10 text-ash"}`} data-testid={`faq-status-${i}`}>
                      {f.active ? "ACTIVE" : "HIDDEN"}
                    </span>
                  </p>
                  <p className="text-xs text-ash mt-1 truncate">{f.answer}</p>
                </div>
                <div className="flex gap-1 shrink-0 flex-wrap justify-end">
                  <button onClick={() => move(f.id, "up")} disabled={i === 0}
                    className="px-2 py-1.5 border border-bone/20 text-bone hover:border-saffron transition-colors disabled:opacity-30" data-testid={`faq-up-${i}`}><ArrowLeft size={12} /></button>
                  <button onClick={() => move(f.id, "down")} disabled={i === items.length - 1}
                    className="px-2 py-1.5 border border-bone/20 text-bone hover:border-saffron transition-colors disabled:opacity-30" data-testid={`faq-down-${i}`}><ArrowRight size={12} /></button>
                  <button onClick={() => toggle(f)}
                    className="text-xs px-2.5 py-1.5 border border-bone/20 text-bone hover:border-saffron transition-colors" data-testid={`faq-toggle-${i}`}>
                    {f.active ? "Hide" : "Show"}
                  </button>
                  <button onClick={() => startEdit(f)}
                    className="text-xs px-2.5 py-1.5 border border-bone/20 text-bone hover:border-saffron transition-colors" data-testid={`faq-edit-${i}`}>Edit</button>
                  <button onClick={() => remove(f.id)}
                    className="px-2 py-1.5 border border-red-400/40 text-red-400 hover:bg-red-400/10 transition-colors" data-testid={`faq-delete-${i}`}><Trash2 size={12} /></button>
                </div>
              </div>
            )}
          </div>
        ))}
        {items.length === 0 && <p className="text-ash text-sm" data-testid="faqs-empty">No FAQs yet.</p>}
      </div>
      <form onSubmit={add} className="space-y-3 border border-dashed border-bone/20 p-4" data-testid="faq-add-form">
        <input required value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })}
          placeholder="Question (e.g. Is sound equipment included?)" className={inputCls} data-testid="faq-question-input" />
        <textarea required rows={2} value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })}
          placeholder="Answer…" className={inputCls} data-testid="faq-answer-input" />
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-xs text-ash cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="faq-active-input" />
            Active (visible on the page)
          </label>
          <button type="submit" disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 border border-saffron/60 text-saffron text-xs font-bold tracking-wide hover:bg-saffron hover:text-ink transition-colors disabled:opacity-50"
            data-testid="faq-add-btn">
            <Plus size={13} /> {saving ? "Adding…" : "Add FAQ"}
          </button>
        </div>
      </form>
    </div>
  );
}

function TestimonialsManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ text: "", name: "", context: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/testimonials").then(({ data }) => setItems(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/testimonials", form);
      toast.success("Testimonial added — live on the private show page.");
      setForm({ text: "", name: "", context: "" });
      load();
    } catch (err) {
      toast.error(apiError(err, "Could not add testimonial"));
    }
    setSaving(false);
  };

  const remove = async (id) => {
    await api.delete(`/admin/testimonials/${id}`);
    load();
  };

  return (
    <div className="border-t border-bone/10 pt-6 space-y-4" data-testid="testimonials-manager">
      <div>
        <p className="text-sm text-bone font-medium">Testimonials</p>
        <p className="text-xs text-ash mt-1">Shown on the Book Private Show page. Add real client words here.</p>
      </div>
      <div className="space-y-3">
        {items.map((t, i) => (
          <div key={t.id} className="border border-bone/10 p-4 flex items-start justify-between gap-3" data-testid={`admin-testimonial-${i}`}>
            <div className="min-w-0">
              <p className="text-sm text-bone/85 italic">&ldquo;{t.text}&rdquo;</p>
              <p className="text-xs text-ash mt-1.5">{t.name}{t.context ? ` — ${t.context}` : ""}</p>
            </div>
            <button onClick={() => remove(t.id)} className="text-ash hover:text-red-400 transition-colors shrink-0" data-testid={`delete-testimonial-${i}`}><Trash2 size={15} /></button>
          </div>
        ))}
        {items.length === 0 && <p className="text-ash text-sm" data-testid="testimonials-empty">No testimonials yet.</p>}
      </div>
      <form onSubmit={add} className="space-y-3 border border-dashed border-bone/20 p-4" data-testid="testimonial-add-form">
        <textarea required rows={2} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="What the client said…" className={inputCls} data-testid="testimonial-text-input" />
        <div className="grid grid-cols-2 gap-3">
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name (e.g. Meera K.)" className={inputCls} data-testid="testimonial-name-input" />
          <input value={form.context} onChange={(e) => setForm({ ...form, context: e.target.value })} placeholder="Context (e.g. Private Show, Udaipur)" className={inputCls} data-testid="testimonial-context-input" />
        </div>
        <button type="submit" disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 border border-saffron/60 text-saffron text-xs font-bold tracking-wide hover:bg-saffron hover:text-ink transition-colors disabled:opacity-50"
          data-testid="testimonial-add-btn">
          <Plus size={13} /> {saving ? "Adding…" : "Add testimonial"}
        </button>
      </form>
    </div>
  );
}

function MarketingTab() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [heroFile, setHeroFile] = useState(null);
  const [aboutFile, setAboutFile] = useState(null);
  const [uploading, setUploading] = useState("");

  useEffect(() => {
    api.get("/settings").then(({ data }) => setForm({
      seo_title: data.seo_title || "", seo_description: data.seo_description || "", pixel_head: data.pixel_head || "",
      hero_image_url: data.hero_image_url || "", about_image_url: data.about_image_url || "",
      about_title: data.about_title || "", about_italic: data.about_italic || "",
      about_ch1_title: data.about_ch1_title || "", about_ch1_text: data.about_ch1_text || "",
      about_ch2_title: data.about_ch2_title || "", about_ch2_text: data.about_ch2_text || "",
      about_ch3_title: data.about_ch3_title || "", about_ch3_text: data.about_ch3_text || "",
      private_show_video_url: data.private_show_video_url || "",
    }));
  }, []);

  if (!form) return <p className="text-ash py-10">Loading…</p>;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const uploadImage = async (file, field, which) => {
    if (!file) return;
    setUploading(which);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/shows/upload-poster", fd);
      setForm({ ...form, [field]: data.image_url });
      if (which === "hero") setHeroFile(null);
      else setAboutFile(null);
      toast.success("Image uploaded — click Save to make it live.");
    } catch (err) {
      toast.error(apiError(err, "Upload failed"));
    }
    setUploading("");
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("Saved — live on the site now.");
    } catch (err) {
      toast.error(apiError(err, "Could not save settings"));
    }
    setSaving(false);
  };

  return (
    <div className="max-w-2xl space-y-12">
    <form onSubmit={save} className="space-y-5" data-testid="marketing-form">
      <Field label="Homepage Hero Image — the big banner photo at the top of the site">
        {(heroFile || form.hero_image_url) && (
          <img src={heroFile ? URL.createObjectURL(heroFile) : fileUrl(form.hero_image_url)} alt="Hero preview"
            className="mb-3 h-40 w-full object-cover border border-bone/15" data-testid="hero-image-preview" />
        )}
        <div className="flex gap-2">
          <input type="file" accept="image/*" onChange={(e) => setHeroFile(e.target.files[0] || null)}
            className="w-full text-sm text-ash file:mr-4 file:px-4 file:py-2.5 file:bg-saffron file:text-ink file:border-0 file:text-sm file:font-medium file:cursor-pointer"
            data-testid="hero-image-input" />
          <button type="button" onClick={() => uploadImage(heroFile, "hero_image_url", "hero")} disabled={!heroFile || uploading === "hero"}
            className="px-5 py-2.5 border border-saffron/60 text-saffron text-xs font-bold tracking-wide hover:bg-saffron hover:text-ink transition-colors disabled:opacity-50 whitespace-nowrap"
            data-testid="hero-upload-btn">
            {uploading === "hero" ? "Uploading…" : "Upload"}
          </button>
        </div>
        <p className="text-xs text-ash mt-1.5">Wide landscape photo works best (e.g. 1800×1000). Upload, then press Save below.</p>
      </Field>
      <Field label="About Section Image — the photo in the 'Devotion, performed' section">
        {(aboutFile || form.about_image_url) && (
          <img src={aboutFile ? URL.createObjectURL(aboutFile) : fileUrl(form.about_image_url)} alt="About preview"
            className="mb-3 h-40 object-cover border border-bone/15" data-testid="about-image-preview" />
        )}
        <div className="flex gap-2">
          <input type="file" accept="image/*" onChange={(e) => setAboutFile(e.target.files[0] || null)}
            className="w-full text-sm text-ash file:mr-4 file:px-4 file:py-2.5 file:bg-saffron file:text-ink file:border-0 file:text-sm file:font-medium file:cursor-pointer"
            data-testid="about-image-input" />
          <button type="button" onClick={() => uploadImage(aboutFile, "about_image_url", "about")} disabled={!aboutFile || uploading === "about"}
            className="px-5 py-2.5 border border-saffron/60 text-saffron text-xs font-bold tracking-wide hover:bg-saffron hover:text-ink transition-colors disabled:opacity-50 whitespace-nowrap"
            data-testid="about-upload-btn">
            {uploading === "about" ? "Uploading…" : "Upload"}
          </button>
        </div>
        <p className="text-xs text-ash mt-1.5">Portrait photo works best here (e.g. 1000×1250). Upload, then press Save below.</p>
      </Field>
      <div className="border-t border-bone/10 pt-5 space-y-4" data-testid="about-content-fields">
        <p className="text-sm text-ash">About section text — the "Devotion, performed" story block. Leave empty to keep the current wording.</p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Heading"><input value={form.about_title} onChange={set("about_title")} placeholder="Devotion, performed" className={inputCls} data-testid="about-title-input" /></Field>
          <Field label="Heading italic part"><input value={form.about_italic} onChange={set("about_italic")} placeholder="without walls" className={inputCls} data-testid="about-italic-input" /></Field>
        </div>
        {[1, 2, 3].map((i) => (
          <div key={i} className="space-y-3 border border-bone/10 p-4">
            <Field label={`Chapter ${i} title`}>
              <input value={form[`about_ch${i}_title`]} onChange={set(`about_ch${i}_title`)} className={inputCls} data-testid={`about-ch${i}-title-input`} />
            </Field>
            <Field label={`Chapter ${i} text`}>
              <textarea rows={3} value={form[`about_ch${i}_text`]} onChange={set(`about_ch${i}_text`)} className={inputCls} data-testid={`about-ch${i}-text-input`} />
            </Field>
          </div>
        ))}
      </div>
      <Field label="Private Show page video — YouTube or Instagram link embedded in the /bookprivateshow hero">
        <input value={form.private_show_video_url} onChange={set("private_show_video_url")}
          placeholder="https://youtube.com/watch?v=… or instagram.com/reel/…" className={inputCls} data-testid="private-video-input" />
        <p className="text-xs text-ash mt-1.5">Leave empty to hide the video. The player is detected automatically — press Save to apply.</p>
      </Field>
      <Field label="SEO Meta Title">
        <input value={form.seo_title} onChange={set("seo_title")} placeholder="The Band Sumiran — Devotional Music, Live" className={inputCls} data-testid="seo-title-input" />
      </Field>
      <Field label="SEO Meta Description">
        <textarea rows={3} value={form.seo_description} onChange={set("seo_description")} placeholder="Book tickets for devotional music sandhyas…" className={inputCls} data-testid="seo-description-input" />
      </Field>
      <Field label="Facebook / Google Pixel code (paste full script tags)">
        <textarea rows={8} value={form.pixel_head} onChange={set("pixel_head")} className={`${inputCls} font-mono text-xs`} placeholder="<script>…pixel code…</script>" data-testid="pixel-input" />
      </Field>
      <button type="submit" disabled={saving} className="flex items-center gap-2 px-8 py-3 bg-saffron text-ink text-sm font-medium hover:bg-gold transition-colors disabled:opacity-50" data-testid="marketing-save-btn">
        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
        {saving ? "Saving…" : "Save"}
      </button>
    </form>
    <TestimonialsManager />
    <FaqsManager />
    </div>
  );
}

const emptyMember = { name: "", role: "", image_url: "" };

function MembersTab() {
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyMember);
  const [photoFile, setPhotoFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/members").then(({ data }) => setMembers(data)), []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const openEdit = (m) => { setEditing(m); setForm({ name: m.name, role: m.role, image_url: m.image_url || "" }); setPhotoFile(null); setOpen(true); };
  const openAdd = () => { setEditing(null); setForm(emptyMember); setPhotoFile(null); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let imageUrl = form.image_url;
      if (photoFile) {
        const fd = new FormData();
        fd.append("file", photoFile);
        const { data: up } = await api.post("/admin/shows/upload-poster", fd);
        imageUrl = up.image_url;
      }
      const payload = { name: form.name, role: form.role, image_url: imageUrl };
      if (editing) await api.put(`/admin/members/${editing.id}`, payload);
      else await api.post("/admin/members", payload);
      toast.success("Member saved.");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err, "Could not save member"));
    }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this member from the website?")) return;
    await api.delete(`/admin/members/${id}`);
    load();
  };

  const move = async (id, direction) => {
    try {
      await api.post(`/admin/members/${id}/move`, null, { params: { direction } });
      load();
    } catch {
      toast.error("Could not reorder");
    }
  };

  return (
    <div data-testid="members-tab">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-ash">Band members shown on the homepage. Upload a photo for each — square photos look best. Without a photo, their initials show in gold.</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button onClick={openAdd} className="flex items-center gap-2 px-5 py-2.5 bg-saffron text-ink text-sm font-medium hover:bg-gold transition-colors" data-testid="add-member-btn">
              <Plus size={15} /> Add Member
            </button>
          </DialogTrigger>
          <DialogContent className="bg-surface border-bone/15 text-bone max-w-md">
            <DialogHeader><DialogTitle className="font-serif text-2xl">{editing ? "Edit Member" : "Add Member"}</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-4 mt-2" data-testid="member-form">
              <Field label="Name"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="member-name-input" /></Field>
              <Field label="Role"><input required value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="e.g. Lead Vocalist" className={inputCls} data-testid="member-role-input" /></Field>
              <Field label="Photo (optional — square works best)">
                <input type="file" accept="image/*" onChange={(e) => setPhotoFile(e.target.files[0] || null)}
                  className="w-full text-sm text-ash file:mr-4 file:px-4 file:py-2.5 file:bg-saffron file:text-ink file:border-0 file:text-sm file:font-medium file:cursor-pointer"
                  data-testid="member-photo-input" />
              </Field>
              {photoFile ? (
                <img src={URL.createObjectURL(photoFile)} alt="Preview" className="h-28 w-28 object-cover border border-bone/15" data-testid="member-photo-preview" />
              ) : form.image_url ? (
                <img src={fileUrl(form.image_url)} alt="Current" className="h-28 w-28 object-cover border border-bone/15" data-testid="member-photo-preview" />
              ) : null}
              <button type="submit" disabled={saving} className="w-full py-3 bg-saffron text-ink text-sm font-medium hover:bg-gold transition-colors disabled:opacity-50" data-testid="member-save-btn">
                {saving ? "Saving…" : "Save Member"}
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {members.map((m, i) => (
          <div key={m.id} className="border border-bone/10 bg-surface" data-testid={`admin-member-${i}`}>
            <div className="aspect-square overflow-hidden">
              {m.image_url ? (
                <img src={fileUrl(m.image_url)} alt={m.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="font-serif text-4xl text-saffron/60">{m.name.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}</span>
                </div>
              )}
            </div>
            <div className="p-3">
              <p className="text-sm text-bone">{m.name}</p>
              <p className="text-[10px] text-saffron tracking-wide uppercase mt-0.5">{m.role}</p>
              <div className="flex gap-2 mt-3 items-center">
                <button onClick={() => openEdit(m)} className="text-xs px-3 py-1.5 border border-bone/20 text-bone hover:border-saffron transition-colors" data-testid={`edit-member-${i}`}>Edit</button>
                <button onClick={() => remove(m.id)} className="text-xs px-3 py-1.5 border border-red-400/40 text-red-400 hover:bg-red-400/10 transition-colors" data-testid={`delete-member-${i}`}>Remove</button>
                <span className="ml-auto flex gap-1">
                  <button onClick={() => move(m.id, "up")} disabled={i === 0} title="Move earlier"
                    className="px-2 py-1.5 border border-bone/20 text-bone hover:border-saffron transition-colors disabled:opacity-30"
                    data-testid={`member-move-up-${i}`}><ArrowLeft size={12} /></button>
                  <button onClick={() => move(m.id, "down")} disabled={i === members.length - 1} title="Move later"
                    className="px-2 py-1.5 border border-bone/20 text-bone hover:border-saffron transition-colors disabled:opacity-30"
                    data-testid={`member-move-down-${i}`}><ArrowRight size={12} /></button>
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const emptyYatraPkg = { name: "", price: "", total_seats: "" };

function YatraTab() {
  const [trip, setTrip] = useState(null);
  const [packages, setPackages] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [savingTrip, setSavingTrip] = useState(false);
  const [pkgOpen, setPkgOpen] = useState(false);
  const [pkgEditing, setPkgEditing] = useState(null);
  const [pkgForm, setPkgForm] = useState(emptyYatraPkg);
  const [pkgSaving, setPkgSaving] = useState(false);
  const [bannerFile, setBannerFile] = useState(null);
  const [uploadingBanner, setUploadingBanner] = useState(false);

  const load = useCallback(() => {
    api.get("/yatra").then(({ data }) => { setTrip(data.trip); setPackages(data.packages); });
    api.get("/admin/yatra/bookings").then(({ data }) => setBookings(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveTrip = async (e) => {
    e.preventDefault();
    setSavingTrip(true);
    try {
      const { title, date, description, itinerary, inclusions, exclusions, multi_date, departure_dates, banner_image_url, itinerary_items } = trip;
      const { data } = await api.put("/admin/yatra", {
        title, date, description, itinerary, inclusions, exclusions,
        multi_date: !!multi_date, departure_dates,
        banner_image_url: banner_image_url || "",
        itinerary_items: (itinerary_items || []).filter((it) => (it.title || "").trim()),
      });
      setTrip(data);
      toast.success("Trip details saved — live on /yatra.");
    } catch (err) {
      toast.error(apiError(err, "Could not save trip"));
    }
    setSavingTrip(false);
  };

  const uploadBanner = async () => {
    if (!bannerFile) return;
    setUploadingBanner(true);
    try {
      const fd = new FormData();
      fd.append("file", bannerFile);
      const { data } = await api.post("/admin/shows/upload-poster", fd);
      setTrip({ ...trip, banner_image_url: data.image_url });
      setBannerFile(null);
      toast.success("Banner uploaded — click Save Trip Details to make it live.");
    } catch (err) {
      toast.error(apiError(err, "Upload failed"));
    }
    setUploadingBanner(false);
  };

  const savePkg = async (e) => {
    e.preventDefault();
    setPkgSaving(true);
    const payload = { name: pkgForm.name, price: Number(pkgForm.price) || 0, total_seats: Number(pkgForm.total_seats) || 0 };
    try {
      if (pkgEditing) await api.put(`/admin/yatra/packages/${pkgEditing.id}`, payload);
      else await api.post("/admin/yatra/packages", payload);
      toast.success("Package saved.");
      setPkgOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err, "Could not save package"));
    }
    setPkgSaving(false);
  };

  const removePkg = async (id) => {
    if (!window.confirm("Delete this package?")) return;
    await api.delete(`/admin/yatra/packages/${id}`);
    load();
  };

  const movePkg = async (id, direction) => {
    await api.post(`/admin/yatra/packages/${id}/move`, null, { params: { direction } });
    load();
  };

  const act = async (id, action) => {
    if (action === "cancel" && !window.confirm("Cancel this booking?")) return;
    try {
      await api.patch(`/admin/bookings/${id}`, { action });
      toast.success(action === "confirm" ? "Booking confirmed — WhatsApp sent." : "Booking cancelled.");
      load();
    } catch (err) {
      toast.error(apiError(err, "Action failed"));
    }
  };

  if (!trip) return <p className="text-ash py-10">Loading…</p>;
  const tset = (k) => (e) => setTrip({ ...trip, [k]: e.target.value });
  const itItems = trip.itinerary_items || [];
  const setItItems = (next) => setTrip({ ...trip, itinerary_items: next });
  const moveItItem = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= itItems.length) return;
    const next = [...itItems];
    [next[i], next[j]] = [next[j], next[i]];
    setItItems(next);
  };

  return (
    <div data-testid="yatra-tab" className="space-y-12">
      <form onSubmit={saveTrip} className="max-w-2xl space-y-4" data-testid="yatra-trip-form">
        <h3 className="font-serif text-2xl text-bone">Trip Details</h3>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Trip title"><input value={trip.title || ""} onChange={tset("title")} className={inputCls} data-testid="yatra-title-input" /></Field>
          <Field label="Date / duration"><input value={trip.date || ""} onChange={tset("date")} placeholder="12–14 Oct 2026" className={inputCls} data-testid="yatra-date-input" /></Field>
        </div>
        <Field label="Description"><textarea rows={3} value={trip.description || ""} onChange={tset("description")} className={inputCls} data-testid="yatra-desc-input" /></Field>
        <Field label="Banner image — full-width photo behind the title at the top of the Yatra page">
          {(bannerFile || trip.banner_image_url) && (
            <img src={bannerFile ? URL.createObjectURL(bannerFile) : fileUrl(trip.banner_image_url)} alt="Banner preview"
              className="mb-3 h-40 w-full object-cover border border-bone/15" data-testid="yatra-banner-preview" />
          )}
          <div className="flex flex-wrap gap-2 items-center">
            <input type="file" accept="image/*" onChange={(e) => setBannerFile(e.target.files[0] || null)}
              className="w-full text-sm text-ash file:mr-4 file:px-4 file:py-2.5 file:bg-saffron file:text-ink file:border-0 file:text-sm file:font-medium file:cursor-pointer"
              data-testid="yatra-banner-input" />
            <button type="button" onClick={uploadBanner} disabled={!bannerFile || uploadingBanner}
              className="px-5 py-2.5 border border-saffron/60 text-saffron text-xs font-bold tracking-wide hover:bg-saffron hover:text-ink transition-colors disabled:opacity-50 whitespace-nowrap"
              data-testid="yatra-banner-upload-btn">
              {uploadingBanner ? "Uploading…" : "Upload"}
            </button>
            {trip.banner_image_url && (
              <button type="button" onClick={() => setTrip({ ...trip, banner_image_url: "" })}
                className="px-5 py-2.5 border border-red-400/40 text-red-400 text-xs font-bold tracking-wide hover:bg-red-400/10 transition-colors whitespace-nowrap"
                data-testid="yatra-banner-remove-btn">Remove</button>
            )}
          </div>
          <p className="text-xs text-ash mt-1.5">Wide landscape photo works best (e.g. 1800×900). Upload, then press Save Trip Details below.</p>
        </Field>
        <Field label="Itinerary — shown as a day-by-day timeline on the Yatra page">
          <div className="space-y-3" data-testid="yatra-itinerary-editor">
            {itItems.map((item, i) => (
              <div key={i} className="border border-bone/10 p-3.5 space-y-2.5" data-testid={`yatra-itinerary-edit-${i}`}>
                <div className="flex items-center gap-2">
                  <input value={item.title} onChange={(e) => setItItems(itItems.map((it, j) => (j === i ? { ...it, title: e.target.value } : it)))}
                    placeholder="Day 1 — Departure & evening kirtan" className={inputCls} data-testid={`yatra-itinerary-title-${i}`} />
                  <div className="flex gap-1 shrink-0">
                    <button type="button" onClick={() => moveItItem(i, -1)} disabled={i === 0}
                      className="px-2 py-1.5 border border-bone/20 text-bone hover:border-saffron transition-colors disabled:opacity-30" data-testid={`yatra-itinerary-up-${i}`}><ArrowLeft size={12} /></button>
                    <button type="button" onClick={() => moveItItem(i, 1)} disabled={i === itItems.length - 1}
                      className="px-2 py-1.5 border border-bone/20 text-bone hover:border-saffron transition-colors disabled:opacity-30" data-testid={`yatra-itinerary-down-${i}`}><ArrowRight size={12} /></button>
                    <button type="button" onClick={() => setItItems(itItems.filter((_, j) => j !== i))}
                      className="px-2 py-1.5 border border-red-400/40 text-red-400 hover:bg-red-400/10 transition-colors" data-testid={`yatra-itinerary-del-${i}`}><Trash2 size={12} /></button>
                  </div>
                </div>
                <textarea rows={3} value={item.description} onChange={(e) => setItItems(itItems.map((it, j) => (j === i ? { ...it, description: e.target.value } : it)))}
                  placeholder="Activities — one per line or comma-separated. Start a line with an emoji to pick its icon; times like 6:30 AM get highlighted." className={inputCls} data-testid={`yatra-itinerary-desc-input-${i}`} />
              </div>
            ))}
            <button type="button" onClick={() => setItItems([...itItems, { title: `Day ${itItems.length + 1}`, description: "" }])}
              className="flex items-center gap-2 px-4 py-2.5 border border-dashed border-bone/25 text-ash text-xs hover:border-saffron hover:text-saffron transition-colors"
              data-testid="yatra-itinerary-add-btn">
              <Plus size={13} /> Add day / segment
            </button>
            {itItems.length === 0 && (
              <p className="text-xs text-ash">No days yet — the itinerary section will be hidden on the public page until you add one.</p>
            )}
          </div>
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Inclusions (one per line)"><textarea rows={4} value={trip.inclusions || ""} onChange={tset("inclusions")} className={inputCls} data-testid="yatra-inclusions-input" /></Field>
          <Field label="Exclusions (one per line)"><textarea rows={4} value={trip.exclusions || ""} onChange={tset("exclusions")} className={inputCls} data-testid="yatra-exclusions-input" /></Field>
        </div>
        <label className="flex items-center gap-2 text-sm text-ash cursor-pointer">
          <input type="checkbox" checked={!!trip.multi_date} onChange={(e) => setTrip({ ...trip, multi_date: e.target.checked })} data-testid="yatra-multidate-input" />
          Multiple departure dates (future flexibility)
        </label>
        {trip.multi_date && (
          <Field label="Departure dates (one per line)">
            <textarea rows={3} value={trip.departure_dates || ""} onChange={tset("departure_dates")} placeholder="12 Oct 2026&#10;26 Oct 2026" className={inputCls} data-testid="yatra-departures-input" />
          </Field>
        )}
        <button type="submit" disabled={savingTrip} className="flex items-center gap-2 px-6 py-3 bg-saffron text-ink text-sm font-medium hover:bg-gold transition-colors disabled:opacity-50" data-testid="yatra-trip-save-btn">
          {savingTrip ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save Trip Details
        </button>
      </form>

      <div>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-serif text-2xl text-bone">Packages</h3>
          <Dialog open={pkgOpen} onOpenChange={setPkgOpen}>
            <DialogTrigger asChild>
              <button onClick={() => { setPkgEditing(null); setPkgForm(emptyYatraPkg); setPkgOpen(true); }}
                className="flex items-center gap-2 px-5 py-2.5 bg-saffron text-ink text-sm font-medium hover:bg-gold transition-colors" data-testid="yatra-add-package-btn">
                <Plus size={15} /> Add Package
              </button>
            </DialogTrigger>
            <DialogContent className="bg-surface border-bone/15 text-bone max-w-md">
              <DialogHeader><DialogTitle className="font-serif text-2xl">{pkgEditing ? "Edit Package" : "Add Package"}</DialogTitle></DialogHeader>
              <form onSubmit={savePkg} className="space-y-4 mt-2" data-testid="yatra-package-form">
                <Field label="Name"><input required value={pkgForm.name} onChange={(e) => setPkgForm({ ...pkgForm, name: e.target.value })} placeholder="Shared Room" className={inputCls} data-testid="yatra-package-name-input" /></Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Price (₹ per person)"><input required type="number" min="1" value={pkgForm.price} onChange={(e) => setPkgForm({ ...pkgForm, price: e.target.value })} className={inputCls} data-testid="yatra-package-price-input" /></Field>
                  <Field label="Total seats"><input required type="number" min="1" value={pkgForm.total_seats} onChange={(e) => setPkgForm({ ...pkgForm, total_seats: e.target.value })} className={inputCls} data-testid="yatra-package-seats-input" /></Field>
                </div>
                <button type="submit" disabled={pkgSaving} className="w-full py-3 bg-saffron text-ink text-sm font-medium hover:bg-gold transition-colors disabled:opacity-50" data-testid="yatra-package-save-btn">
                  {pkgSaving ? "Saving…" : "Save Package"}
                </button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        <div className="space-y-3">
          {packages.map((p, i) => (
            <div key={p.id} className="flex items-center justify-between gap-4 border border-bone/10 p-4" data-testid={`yatra-admin-package-${i}`}>
              <div>
                <p className="text-bone font-medium">{p.name} <span className="text-gold">₹{p.price.toLocaleString("en-IN")}</span></p>
                <p className="text-xs text-ash mt-1">{p.remaining_seats} of {p.total_seats} seats left{p.seats_sold ? ` · ${p.seats_sold} booked` : ""}</p>
              </div>
              <div className="flex gap-2 items-center shrink-0">
                <button onClick={() => { setPkgEditing(p); setPkgForm({ name: p.name, price: p.price, total_seats: p.total_seats }); setPkgOpen(true); }}
                  className="text-xs px-3 py-1.5 border border-bone/20 text-bone hover:border-saffron transition-colors" data-testid={`yatra-edit-package-${i}`}>Edit</button>
                <button onClick={() => removePkg(p.id)} className="text-xs px-3 py-1.5 border border-red-400/40 text-red-400 hover:bg-red-400/10 transition-colors" data-testid={`yatra-delete-package-${i}`}>Delete</button>
                <span className="flex gap-1 ml-1">
                  <button onClick={() => movePkg(p.id, "up")} disabled={i === 0} className="px-2 py-1.5 border border-bone/20 text-bone hover:border-saffron transition-colors disabled:opacity-30" data-testid={`yatra-package-up-${i}`}><ArrowLeft size={12} /></button>
                  <button onClick={() => movePkg(p.id, "down")} disabled={i === packages.length - 1} className="px-2 py-1.5 border border-bone/20 text-bone hover:border-saffron transition-colors disabled:opacity-30" data-testid={`yatra-package-down-${i}`}><ArrowRight size={12} /></button>
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-serif text-2xl text-bone mb-5">Yatra Bookings</h3>
        {bookings.length === 0 && <p className="text-ash text-sm py-6">No yatra bookings yet.</p>}
        {bookings.length > 0 && (
          <div className="overflow-x-auto border border-bone/10">
            <table className="w-full text-sm" data-testid="yatra-bookings-table">
              <thead>
                <tr className="border-b border-bone/10 text-left">
                  {["Guest", "Package", "Payment", "Status", "Code", ""].map((h) => (
                    <th key={h} className="p-4 text-[10px] tracking-[0.2em] uppercase text-ash font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bookings.map((b, i) => (
                  <tr key={b.id} className="border-b border-bone/5" data-testid={`yatra-booking-${i}`}>
                    <td className="p-4"><p className="text-bone">{b.name}</p><p className="text-xs text-ash">{b.phone}</p></td>
                    <td className="p-4 text-bone/80">{b.tier} × {b.quantity}</td>
                    <td className="p-4 text-bone/80 whitespace-nowrap">
                      ₹{(b.amount / 100).toLocaleString("en-IN")} {b.payment_option === "advance" ? <span className="text-xs text-saffron">(advance · bal ₹{((b.balance_due || 0) / 100).toLocaleString("en-IN")})</span> : <span className="text-xs text-ash">(full)</span>}
                    </td>
                    <td className="p-4">
                      <span className={`text-[10px] font-display px-1.5 py-0.5 ${b.status === "confirmed" ? "bg-saffron text-ink" : b.status === "cancelled" ? "bg-red-500/20 text-red-400" : "bg-bone/10 text-ash"}`}>
                        {b.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="p-4 font-mono text-xs text-gold">{b.ticket_code || "—"}</td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        {b.status === "pending" && (
                          <button onClick={() => act(b.id, "confirm")} className="text-xs px-3 py-1.5 border border-saffron/50 text-saffron hover:bg-saffron hover:text-ink transition-colors whitespace-nowrap" data-testid={`yatra-confirm-${i}`}>Confirm</button>
                        )}
                        {b.status !== "cancelled" && (
                          <button onClick={() => act(b.id, "cancel")} className="text-xs px-3 py-1.5 border border-red-400/40 text-red-400 hover:bg-red-400/10 transition-colors" data-testid={`yatra-cancel-${i}`}>Cancel</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const LEAD_FIELD_TYPES = [
  ["short_text", "Short text"], ["long_text", "Long text"], ["radio", "Multiple choice (radio)"],
  ["checkboxes", "Checkboxes"], ["dropdown", "Dropdown"], ["date", "Date picker"],
  ["phone", "Phone number"], ["email", "Email"],
];
const LEAD_CHOICE_TYPES = ["radio", "checkboxes", "dropdown"];
const LEAD_TEXT_TYPES = ["short_text", "long_text", "phone", "email"];
const leadTypeLabel = (t) => LEAD_FIELD_TYPES.find(([v]) => v === t)?.[1] || t;
const emptyLeadForm = { name: "", active: true, fields: [] };

function LeadFormsTab() {
  const [forms, setForms] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyLeadForm);
  const [saving, setSaving] = useState(false);
  const [newFieldType, setNewFieldType] = useState("short_text");
  const [subsFor, setSubsFor] = useState(null);
  const [subs, setSubs] = useState([]);
  const [subsLoading, setSubsLoading] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/lead-forms").then(({ data }) => setForms(data)).catch(() => setForms([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(emptyLeadForm); setEditorOpen(true); };
  const openEdit = (f) => {
    setEditing(f);
    setForm({ name: f.name, active: f.active, fields: f.fields.map((x) => ({ ...x, options: [...(x.options || [])] })) });
    setEditorOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/lead-forms/${editing.id}`, form);
      else await api.post("/admin/lead-forms", form);
      toast.success("Form saved.");
      setEditorOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err, "Could not save form"));
    }
    setSaving(false);
  };

  const removeForm = async (f) => {
    if (!window.confirm(`Delete "${f.name}" and all its submissions?`)) return;
    await api.delete(`/admin/lead-forms/${f.id}`);
    load();
  };

  const toggleActive = async (f) => {
    await api.put(`/admin/lead-forms/${f.id}`, { name: f.name, active: !f.active, fields: f.fields });
    load();
  };

  const copyLink = (f) => {
    navigator.clipboard.writeText(`${window.location.origin}/l/${f.slug}`)
      .then(() => toast.success("Form link copied"))
      .catch(() => toast.error("Could not copy"));
  };

  const viewSubs = async (f) => {
    setSubsFor(f);
    setSubsLoading(true);
    try {
      const { data } = await api.get(`/admin/lead-forms/${f.id}/submissions`);
      setSubs(data);
    } catch { setSubs([]); }
    setSubsLoading(false);
  };

  const fields = form.fields;
  const setFields = (next) => setForm({ ...form, fields: next });
  const updField = (i, patch) => setFields(fields.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const moveField = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= fields.length) return;
    const next = [...fields];
    [next[i], next[j]] = [next[j], next[i]];
    setFields(next);
  };

  if (!forms) return <p className="text-ash py-10">Loading…</p>;

  return (
    <div data-testid="lead-forms-tab" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-serif text-2xl text-bone">Lead Forms</h3>
          <p className="text-xs text-ash mt-1">Public forms at /l/&lt;slug&gt; — not linked anywhere on the site. Share the link directly.</p>
        </div>
        <button onClick={openNew}
          className="flex items-center gap-2 px-5 py-2.5 bg-saffron text-ink text-sm font-medium hover:bg-gold transition-colors"
          data-testid="lead-form-create-btn">
          <Plus size={15} /> New Form
        </button>
      </div>

      {forms.length === 0 && <p className="text-ash text-sm py-6" data-testid="lead-forms-empty">No forms yet — create your first lead form.</p>}

      <div className="space-y-3">
        {forms.map((f, i) => (
          <div key={f.id} className="border border-bone/10 p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`lead-form-row-${i}`}>
            <div className="min-w-0">
              <p className="text-bone font-medium flex items-center gap-2 flex-wrap">
                {f.name}
                <span className={`text-[10px] font-display px-1.5 py-0.5 ${f.active ? "bg-saffron text-ink" : "bg-bone/10 text-ash"}`} data-testid={`lead-form-status-${i}`}>
                  {f.active ? "ACTIVE" : "INACTIVE"}
                </span>
              </p>
              <p className="text-xs text-ash mt-1 font-mono">/l/{f.slug} · {f.fields.length} field{f.fields.length !== 1 ? "s" : ""} · {f.submission_count} submission{f.submission_count !== 1 ? "s" : ""}</p>
            </div>
            <div className="flex gap-2 items-center flex-wrap shrink-0">
              <button onClick={() => copyLink(f)} title="Copy public link"
                className="text-xs px-3 py-1.5 border border-bone/20 text-bone hover:border-saffron transition-colors flex items-center gap-1.5" data-testid={`lead-form-copy-${i}`}>
                <Copy size={12} /> Link
              </button>
              <button onClick={() => viewSubs(f)}
                className="text-xs px-3 py-1.5 border border-bone/20 text-bone hover:border-saffron transition-colors flex items-center gap-1.5" data-testid={`lead-form-subs-${i}`}>
                <ClipboardList size={12} /> Submissions
              </button>
              <button onClick={() => toggleActive(f)}
                className="text-xs px-3 py-1.5 border border-bone/20 text-bone hover:border-saffron transition-colors" data-testid={`lead-form-toggle-${i}`}>
                {f.active ? "Deactivate" : "Activate"}
              </button>
              <button onClick={() => openEdit(f)}
                className="text-xs px-3 py-1.5 border border-bone/20 text-bone hover:border-saffron transition-colors" data-testid={`lead-form-edit-${i}`}>Edit</button>
              <button onClick={() => removeForm(f)}
                className="text-xs px-3 py-1.5 border border-red-400/40 text-red-400 hover:bg-red-400/10 transition-colors" data-testid={`lead-form-delete-${i}`}>Delete</button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="bg-surface border-bone/15 text-bone max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editing ? "Edit Form" : "New Lead Form"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-5 mt-2" data-testid="lead-form-editor">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Form name"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Wedding Enquiry" className={inputCls} data-testid="lead-form-name-input" /></Field>
              <label className="flex items-center gap-2 text-sm text-ash cursor-pointer self-end pb-2.5">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="lead-form-active-input" />
                Active (accepting submissions)
              </label>
            </div>

            <div className="space-y-3">
              <p className={labelCls}>Fields ({fields.length})</p>
              {fields.map((f, i) => (
                <div key={i} className="border border-bone/10 p-3.5 space-y-2.5" data-testid={`lead-field-edit-${i}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-display tracking-wider uppercase text-saffron border border-saffron/40 px-2 py-1 shrink-0">{leadTypeLabel(f.type)}</span>
                    <input value={f.label} onChange={(e) => updField(i, { label: e.target.value })} placeholder="Field label" className={inputCls} data-testid={`lead-field-label-${i}`} />
                    <div className="flex gap-1 shrink-0">
                      <button type="button" onClick={() => moveField(i, -1)} disabled={i === 0}
                        className="px-2 py-1.5 border border-bone/20 text-bone hover:border-saffron transition-colors disabled:opacity-30" data-testid={`lead-field-up-${i}`}><ArrowLeft size={12} /></button>
                      <button type="button" onClick={() => moveField(i, 1)} disabled={i === fields.length - 1}
                        className="px-2 py-1.5 border border-bone/20 text-bone hover:border-saffron transition-colors disabled:opacity-30" data-testid={`lead-field-down-${i}`}><ArrowRight size={12} /></button>
                      <button type="button" onClick={() => setFields(fields.filter((_, j) => j !== i))}
                        className="px-2 py-1.5 border border-red-400/40 text-red-400 hover:bg-red-400/10 transition-colors" data-testid={`lead-field-del-${i}`}><Trash2 size={12} /></button>
                    </div>
                  </div>
                  {LEAD_CHOICE_TYPES.includes(f.type) && (
                    <textarea rows={2} value={(f.options || []).join("\n")} onChange={(e) => updField(i, { options: e.target.value.split("\n") })}
                      placeholder="One option per line" className={inputCls} data-testid={`lead-field-options-${i}`} />
                  )}
                  {LEAD_TEXT_TYPES.includes(f.type) && (
                    <input value={f.placeholder || ""} onChange={(e) => updField(i, { placeholder: e.target.value })}
                      placeholder="Placeholder text (optional)" className={inputCls} data-testid={`lead-field-placeholder-${i}`} />
                  )}
                  <label className="flex items-center gap-2 text-xs text-ash cursor-pointer">
                    <input type="checkbox" checked={!!f.required} onChange={(e) => updField(i, { required: e.target.checked })} data-testid={`lead-field-required-${i}`} />
                    Required field
                  </label>
                </div>
              ))}
              <div className="flex gap-2">
                <select value={newFieldType} onChange={(e) => setNewFieldType(e.target.value)}
                  className="bg-ink border border-bone/15 px-3 py-2.5 text-sm text-bone focus:border-saffron focus:outline-none [color-scheme:dark]" data-testid="lead-field-type-select">
                  {LEAD_FIELD_TYPES.map(([v, l]) => <option key={v} value={v} className="bg-ink">{l}</option>)}
                </select>
                <button type="button" onClick={() => setFields([...fields, { id: "", type: newFieldType, label: "", required: false, options: [], placeholder: "" }])}
                  className="flex items-center gap-2 px-4 py-2.5 border border-dashed border-bone/25 text-ash text-xs hover:border-saffron hover:text-saffron transition-colors"
                  data-testid="lead-field-add-btn">
                  <Plus size={13} /> Add field
                </button>
              </div>
            </div>

            <button type="submit" disabled={saving}
              className="w-full py-3 bg-saffron text-ink text-sm font-medium hover:bg-gold transition-colors disabled:opacity-50" data-testid="lead-form-save-btn">
              {saving ? "Saving…" : "Save Form"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!subsFor} onOpenChange={(o) => !o && setSubsFor(null)}>
        <DialogContent className="bg-surface border-bone/15 text-bone max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Submissions — {subsFor?.name}</DialogTitle></DialogHeader>
          <div className="mt-2" data-testid="lead-submissions">
            {subsLoading && <p className="text-ash text-sm py-6">Loading…</p>}
            {!subsLoading && subs.length === 0 && <p className="text-ash text-sm py-6" data-testid="lead-subs-empty">No submissions yet.</p>}
            <div className="space-y-3">
              {subs.map((s, i) => (
                <div key={s.id} className="border border-bone/10 p-4" data-testid={`lead-sub-${i}`}>
                  <p className="text-[11px] text-saffron font-display tracking-wider" data-testid={`lead-sub-time-${i}`}>
                    {new Date(s.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })} IST
                  </p>
                  <dl className="mt-2.5 space-y-1.5">
                    {subsFor.fields.map((f) => (
                      <div key={f.id} className="flex gap-3 text-sm">
                        <dt className="text-ash w-36 shrink-0">{f.label}</dt>
                        <dd className="text-bone/85 break-words" data-testid={`lead-sub-${i}-value-${f.id}`}>
                          {Array.isArray(s.values[f.id]) ? s.values[f.id].join(", ") : (s.values[f.id] || "—")}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const emptyCoupon = { code: "", discount_type: "percent", value: 10, active: true, visible: false };

function CouponsTab() {
  const [coupons, setCoupons] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyCoupon);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/admin/coupons").then(({ data }) => setCoupons(data)), []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const openEdit = (c) => {
    setEditing(c);
    setForm({ code: c.code, discount_type: c.discount_type, value: c.value, active: c.active, visible: !!c.visible });
    setOpen(true);
  };
  const openAdd = () => { setEditing(null); setForm(emptyCoupon); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, code: form.code.trim().toUpperCase(), value: Number(form.value) || 0, active: !!form.active };
    try {
      if (editing) await api.put(`/admin/coupons/${editing.id}`, payload);
      else await api.post("/admin/coupons", payload);
      toast.success("Coupon saved.");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err, "Could not save coupon"));
    }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this coupon?")) return;
    await api.delete(`/admin/coupons/${id}`);
    load();
  };

  return (
    <div data-testid="coupons-tab">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-ash">Create promo codes for ticket checkout — percentage or fixed ₹ off. Only active coupons work at checkout.</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button onClick={openAdd} className="flex items-center gap-2 px-5 py-2.5 bg-saffron text-ink text-sm font-medium hover:bg-gold transition-colors" data-testid="add-coupon-btn">
              <Plus size={15} /> Add Coupon
            </button>
          </DialogTrigger>
          <DialogContent className="bg-surface border-bone/15 text-bone max-w-md">
            <DialogHeader><DialogTitle className="font-serif text-2xl">{editing ? "Edit Coupon" : "Add Coupon"}</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-4 mt-2" data-testid="coupon-form">
              <Field label="Coupon Code">
                <input required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  placeholder="e.g. BHAKTI10" className={`${inputCls} tracking-widest uppercase`} data-testid="coupon-code-input" />
              </Field>
              <Field label="Discount Type">
                <div className="grid grid-cols-2 gap-2" data-testid="coupon-type-picker">
                  {[["percent", "Percentage Off"], ["fixed", "Fixed Amount Off"]].map(([v, label]) => (
                    <button type="button" key={v} onClick={() => setForm({ ...form, discount_type: v })}
                      className={`py-3 text-xs font-bold tracking-wide border transition-colors ${form.discount_type === v ? "border-saffron bg-saffron/10 text-saffron" : "border-bone/15 text-ash hover:border-bone/35"}`}
                      data-testid={`coupon-type-${v}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label={form.discount_type === "percent" ? "Discount (%)" : "Discount (₹)"}>
                <input required type="number" min="1" max={form.discount_type === "percent" ? 100 : undefined}
                  value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })}
                  className={inputCls} data-testid="coupon-value-input" />
              </Field>
              <label className="flex items-center gap-2 text-sm text-ash cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="coupon-active-input" />
                Active (usable at checkout)
              </label>
              <label className="flex items-center gap-2 text-sm text-ash cursor-pointer">
                <input type="checkbox" checked={form.visible} onChange={(e) => setForm({ ...form, visible: e.target.checked })} data-testid="coupon-visible-input" />
                Visible on Checkout (shown as a tappable offer on the booking page)
              </label>
              <button type="submit" disabled={saving} className="w-full py-3 bg-saffron text-ink text-sm font-medium hover:bg-gold transition-colors disabled:opacity-50" data-testid="coupon-save-btn">
                {saving ? "Saving…" : "Save Coupon"}
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      {coupons.length === 0 && <p className="text-ash py-8">No coupons yet.</p>}
      <div className="space-y-3">
        {coupons.map((c, i) => (
          <div key={c.id} className="flex items-center justify-between gap-4 border border-bone/10 p-4" data-testid={`admin-coupon-${i}`}>
            <div className="flex items-center gap-3">
              <BadgePercent size={18} className="text-saffron shrink-0" />
              <div>
                <p className="text-bone font-medium tracking-widest">{c.code}
                  {c.active
                    ? <span className="text-[10px] font-display text-ink bg-saffron px-1.5 py-0.5 ml-2">ACTIVE</span>
                    : <span className="text-xs text-ash ml-2">(disabled)</span>}
                  {c.visible && <span className="text-[10px] font-display text-saffron border border-saffron/50 px-1.5 py-0.5 ml-2">ON CHECKOUT</span>}
                </p>
                <p className="text-xs text-ash mt-1">{c.discount_type === "percent" ? `${c.value}% off` : `₹${c.value.toLocaleString("en-IN")} off`}</p>
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => openEdit(c)} className="text-xs px-3 py-1.5 border border-bone/20 text-bone hover:border-saffron transition-colors" data-testid={`edit-coupon-${i}`}>Edit</button>
              <button onClick={() => remove(c.id)} className="text-xs px-3 py-1.5 border border-red-400/40 text-red-400 hover:bg-red-400/10 transition-colors" data-testid={`delete-coupon-${i}`}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const PAGE_LABELS = {
  "/": "Home", "/upcomingshows": "Upcoming Shows", "/yatra": "Vrindavan Yatra",
  "/book": "Book Tickets", "/private": "Private Shows", "/bookprivateshow": "Book Private Show",
  "/contact": "Contact", "/terms": "Terms", "/privacy": "Privacy", "/refund": "Refunds",
};
const pageLabel = (p) => PAGE_LABELS[p] || (p.startsWith("/book/") ? `Show: ${p.slice(6)}` : p.startsWith("/l/") ? `Lead form: ${p.slice(3)}` : p);

function VisitorStats() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/analytics/visitors").then(({ data }) => setStats(data)).catch(() => {});
  }, []);
  if (!stats) return null;
  const cards = [["today", "Today"], ["week", "Last 7 days"], ["total", "All time"]];
  return (
    <div className="mb-8 space-y-3" data-testid="visitor-stats">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {cards.map(([key, label]) => (
          <div key={key} className="border border-bone/10 bg-ink px-5 py-4" data-testid={`visitor-stats-${key}`}>
            <p className="text-[10px] font-display tracking-[0.2em] uppercase text-ash">{label}</p>
            <p className="mt-1.5 font-serif text-3xl text-gold">{stats[key].unique}
              <span className="ml-2 text-xs text-ash font-sans">unique · {stats[key].visits} visits</span>
            </p>
          </div>
        ))}
      </div>
      {stats.pages?.length > 0 && (
        <div className="border border-bone/10 bg-ink px-5 py-4" data-testid="visitor-stats-pages">
          <p className="text-[10px] font-display tracking-[0.2em] uppercase text-ash">Popular pages — last 7 days</p>
          <div className="mt-3 space-y-1.5">
            {stats.pages.map((p, i) => (
              <div key={p.path} className="flex items-center justify-between gap-4 text-sm" data-testid={`visitor-page-${i}`}>
                <span className="text-bone/80 truncate">{pageLabel(p.path)}</span>
                <span className="text-xs text-ash shrink-0"><span className="text-gold font-medium">{p.unique}</span> unique · {p.visits} visits</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => {
      if (data.role !== "admin") { navigate("/gate"); return; }
      setAuthed(true);
    }).catch(() => {
      localStorage.removeItem("sumiran_token");
      navigate("/admin");
    });
  }, [navigate]);

  if (!authed) {
    return <div className="min-h-screen bg-ink text-ash flex items-center justify-center" data-testid="admin-loading">Checking session…</div>;
  }

  return (
    <div className="min-h-screen bg-ink text-bone" data-testid="admin-dashboard">
      <header className="border-b border-bone/10 px-6 md:px-10 h-16 flex items-center justify-between sticky top-0 bg-ink/90 backdrop-blur-xl z-40">
        <div className="flex items-baseline gap-2">
          <span className="font-hindi text-2xl text-saffron">सुमिरन</span>
          <span className="font-display text-xs tracking-[0.25em] text-bone">ADMIN</span>
        </div>
        <div className="flex items-center gap-5">
          <Link to="/gate" className="text-xs text-saffron hover:text-gold transition-colors" data-testid="admin-gate-link">Gate Scanner</Link>
          <a href="/" className="text-xs text-ash hover:text-saffron transition-colors" data-testid="admin-view-site">View Site</a>
          <button onClick={() => { localStorage.removeItem("sumiran_token"); navigate("/admin"); }}
            className="flex items-center gap-2 text-xs text-ash hover:text-red-400 transition-colors" data-testid="admin-logout-btn">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>
      <main className="px-6 md:px-10 py-8">
        <VisitorStats />
        <Tabs defaultValue="event">
          <TabsList className="bg-surface border border-bone/10 rounded-none h-11 flex-wrap h-auto">
            <TabsTrigger value="event" className="rounded-none data-[state=active]:bg-saffron data-[state=active]:text-ink" data-testid="tab-event">Event</TabsTrigger>
            <TabsTrigger value="shows" className="rounded-none data-[state=active]:bg-saffron data-[state=active]:text-ink" data-testid="tab-shows">Shows</TabsTrigger>
            <TabsTrigger value="packages" className="rounded-none data-[state=active]:bg-saffron data-[state=active]:text-ink" data-testid="tab-packages">Packages</TabsTrigger>
            <TabsTrigger value="videos" className="rounded-none data-[state=active]:bg-saffron data-[state=active]:text-ink" data-testid="tab-videos">Videos</TabsTrigger>
            <TabsTrigger value="gallery" className="rounded-none data-[state=active]:bg-saffron data-[state=active]:text-ink" data-testid="tab-gallery">Gallery</TabsTrigger>
            <TabsTrigger value="members" className="rounded-none data-[state=active]:bg-saffron data-[state=active]:text-ink" data-testid="tab-members">Members</TabsTrigger>
            <TabsTrigger value="yatra" className="rounded-none data-[state=active]:bg-saffron data-[state=active]:text-ink" data-testid="tab-yatra">Yatra</TabsTrigger>
            <TabsTrigger value="enquiries" className="rounded-none data-[state=active]:bg-saffron data-[state=active]:text-ink" data-testid="tab-enquiries">Enquiries</TabsTrigger>
            <TabsTrigger value="guests" className="rounded-none data-[state=active]:bg-saffron data-[state=active]:text-ink" data-testid="tab-guests">Guest List</TabsTrigger>
            <TabsTrigger value="coupons" className="rounded-none data-[state=active]:bg-saffron data-[state=active]:text-ink" data-testid="tab-coupons">Coupons</TabsTrigger>
            <TabsTrigger value="leadforms" className="rounded-none data-[state=active]:bg-saffron data-[state=active]:text-ink" data-testid="tab-leadforms">Lead Forms</TabsTrigger>
            <TabsTrigger value="marketing" className="rounded-none data-[state=active]:bg-saffron data-[state=active]:text-ink" data-testid="tab-marketing">Marketing & SEO</TabsTrigger>
          </TabsList>
          <div className="mt-8">
            <TabsContent value="event"><EventTab /></TabsContent>
            <TabsContent value="shows"><ShowsTab /></TabsContent>
            <TabsContent value="packages"><PackagesTab /></TabsContent>
            <TabsContent value="videos"><VideosTab /></TabsContent>
            <TabsContent value="gallery"><GalleryTab /></TabsContent>
            <TabsContent value="members"><MembersTab /></TabsContent>
            <TabsContent value="yatra"><YatraTab /></TabsContent>
            <TabsContent value="enquiries"><EnquiriesTab /></TabsContent>
            <TabsContent value="guests"><GuestsTab /></TabsContent>
            <TabsContent value="coupons"><CouponsTab /></TabsContent>
            <TabsContent value="leadforms"><LeadFormsTab /></TabsContent>
            <TabsContent value="marketing"><MarketingTab /></TabsContent>
          </div>
        </Tabs>
      </main>
    </div>
  );
}
